import { startOfToday } from 'date-fns';
import { useMemo } from 'react';
import {
  ClientType,
  type ReferralPostBody,
} from '@/common/hooks/referrals/types';
import { useOptionalRouteParam } from '@/common/hooks/useOptionalRouteParam';
import { getIsSending } from '@/views/Referrals/utils';
import {
  type AgentFields,
  baseAgentFields,
  type FieldValues,
} from '@/views/Referrals/views/NewReferral/types';
import { type IdentityRequest, useIdentity } from '$api-clients/hooks';

type ReferralClientTypes = NonNullable<
  ReferralPostBody['client']['referralClientTypes']
>;

const clientTypeToReferralClientType: Record<
  ClientType,
  keyof ReferralClientTypes
> = {
  [ClientType.Buyer]: 'referralBuyer',
  [ClientType.Seller]: 'referralSeller',
  [ClientType.Landlord]: 'referralLandlord',
  [ClientType.Tenant]: 'referralTenant',
};

export function buildClientPayload(
  clientData: FieldValues['client'],
): ReferralPostBody['client'] {
  const referralClientTypes: ReferralClientTypes = {
    referralBuyer: 'false',
    referralSeller: 'false',
    referralLandlord: 'false',
    referralTenant: 'false',
  };

  for (const clientType of clientData.clientTypesList) {
    const referralClientType =
      clientTypeToReferralClientType[clientType] ?? clientType;
    if (referralClientType in referralClientTypes) {
      referralClientTypes[referralClientType] = 'true';
    }
  }

  const { clientTypesList: _clientTypesList, id: _id, ...rest } = clientData;

  return {
    ...rest,
    referralClientTypes,
    type: 'client',
  };
}

export function buildReferralPostBody(
  formData: FieldValues,
): ReferralPostBody | null {
  const { referringAgent, transactingAgent } = formData;
  if (!referringAgent || !transactingAgent) {
    return null;
  }

  const isSending = getIsSending({ referralType: formData.referralType });

  const primaryAgent = {
    ...(isSending ? referringAgent : transactingAgent),
    type: 'agent' as const,
  };
  const secondaryAgent = {
    ...(isSending ? transactingAgent : referringAgent),
    type: 'counterpartyAgent' as const,
  };

  // For external agents, we only ask for one broker so the broker and the supervising broker are the same.
  if (!secondaryAgent.userId) {
    secondaryAgent.broker = secondaryAgent.supervisingBroker || null;
  }

  return {
    owner: primaryAgent.userId,
    teamId: primaryAgent.teamId,
    referringAgent: isSending ? primaryAgent : secondaryAgent,
    transactingAgent: isSending ? secondaryAgent : primaryAgent,
    client: buildClientPayload(formData.client),
    referralType: formData.referralType,
    notes: formData.notes,
  };
}

type IdentityTeamMember =
  IdentityRequest<'/teams/{teamId}/members/{userId}'>['Data'];

type IdentityTeam = IdentityRequest<'/teams/{teamId}'>['Data'];

function licenseIsNotExpired(license: IdentityTeamMember['license']) {
  if (!license) {
    return false;
  }

  if (!license.expiration) {
    return true;
  }

  const expirationDate = new Date(license.expiration);
  return expirationDate > startOfToday();
}

function isValidAgent(member: IdentityTeamMember) {
  const isAgent = member.roles.includes('AGENT');
  const isActive = member.status === 'ACTIVE';

  return isAgent && isActive && licenseIsNotExpired(member.license);
}

function mapToAgentFields(
  member: IdentityTeamMember,
  team: IdentityTeam,
): AgentFields {
  return {
    ...baseAgentFields,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.contact.email ?? '',
    cellPhone: member.contact.phone ?? '',
    state: member.address?.state ?? '',
    licenseNumber: member.license?.number ?? '',
    licenseState: member.license?.state ?? '',
    userId: member.userId,
    teamId: team.id,
    isSideAgent: true,
    brokerage: {
      ...baseAgentFields.brokerage,
      companyName: team.legalName ?? '',
    },
    supervisingBroker: {
      ...baseAgentFields.supervisingBroker,
      firstName: team.supervisingBroker?.firstName ?? '',
      lastName: team.supervisingBroker?.lastName ?? '',
    },
  };
}

export function useDefaultTransactingAgent() {
  const userId = useOptionalRouteParam('userId');
  const teamId = useOptionalRouteParam('teamId');

  const { data: team, isLoading: teamLoading } = useIdentity(
    '/teams/{teamId}',
    teamId && userId ? { params: { path: { teamId } } } : null,
  );

  const { data: member, isLoading: memberLoading } = useIdentity(
    '/teams/{teamId}/members/{userId}',
    teamId && userId ? { params: { path: { teamId, userId } } } : null,
  );

  const agent = useMemo(() => {
    if (!team || !member) {
      return null;
    }

    if (!isValidAgent(member)) {
      return null;
    }

    return mapToAgentFields(member, team);
  }, [team, member]);

  return {
    loading: teamLoading || memberLoading,
    agent,
  };
}
